import {
  EXCLUSIVE_DUTCH_ORDER_REACTOR_ABI,
  EXCLUSIVE_DUTCH_ORDER_REACTOR_BYTECODE,
  ORDER_QUOTER_ABI,
  ORDER_QUOTER_BYTECODE,
  PRIORITY_ORDER_REACTOR_ABI,
  PRIORITY_ORDER_REACTOR_BYTECODE,
  V2_DUTCH_ORDER_REACTOR_ABI,
  V2_DUTCH_ORDER_REACTOR_BYTECODE,
} from "@gotts.ai/chain";

import { deployContract } from "./deploy-utils";

import type { DeployContext } from "./deploy-utils";
import type {
  DeploymentPhaseResult,
  FoundationAddresses,
  UniswapXAddresses,
} from "./types";
import type { Hex } from "viem";

/**
 * Phase 6: Deploy UniswapX reactor contracts.
 *
 * Deploys the full UniswapX stack:
 *   1. ExclusiveDutchOrderReactor — Dutch auction orders with exclusive filler
 *   2. PriorityOrderReactor — priority fee-based ordering
 *   3. V2DutchOrderReactor — improved Dutch auction mechanism
 *   4. OrderQuoter — off-chain quote simulation for orders
 *
 * All reactors take (IPermit2, protocolFeeOwner) as constructor args.
 * OrderQuoter has no constructor args.
 *
 * @param ctx - Deploy context
 * @param foundation - Phase 1 addresses (Permit2)
 */
export async function deployUniswapX(
  ctx: DeployContext,
  foundation: FoundationAddresses,
): Promise<DeploymentPhaseResult<UniswapXAddresses>> {
  const start = Date.now();
  let totalGas = 0n;
  const txHashes: `0x${string}`[] = [];

  // 1. ExclusiveDutchOrderReactor — constructor(IPermit2, address protocolFeeOwner)
  const exclusiveDutchResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: EXCLUSIVE_DUTCH_ORDER_REACTOR_ABI,
      bytecode: EXCLUSIVE_DUTCH_ORDER_REACTOR_BYTECODE as Hex,
    },
    [foundation.permit2, ctx.deployer],
  );
  totalGas += exclusiveDutchResult.gasUsed;
  txHashes.push(exclusiveDutchResult.txHash);

  // 2. PriorityOrderReactor — constructor(IPermit2, address protocolFeeOwner)
  const priorityResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: PRIORITY_ORDER_REACTOR_ABI,
      bytecode: PRIORITY_ORDER_REACTOR_BYTECODE as Hex,
    },
    [foundation.permit2, ctx.deployer],
  );
  totalGas += priorityResult.gasUsed;
  txHashes.push(priorityResult.txHash);

  // 3. V2DutchOrderReactor — constructor(IPermit2, address protocolFeeOwner)
  const v2DutchResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: V2_DUTCH_ORDER_REACTOR_ABI,
      bytecode: V2_DUTCH_ORDER_REACTOR_BYTECODE as Hex,
    },
    [foundation.permit2, ctx.deployer],
  );
  totalGas += v2DutchResult.gasUsed;
  txHashes.push(v2DutchResult.txHash);

  // 4. OrderQuoter — no constructor args
  const orderQuoterResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    {
      abi: ORDER_QUOTER_ABI,
      bytecode: ORDER_QUOTER_BYTECODE as Hex,
    },
  );
  totalGas += orderQuoterResult.gasUsed;
  txHashes.push(orderQuoterResult.txHash);

  return {
    phase: 6,
    name: "UniswapX",
    addresses: {
      exclusiveDutchOrderReactor: exclusiveDutchResult.address,
      priorityOrderReactor: priorityResult.address,
      v2DutchOrderReactor: v2DutchResult.address,
      orderQuoter: orderQuoterResult.address,
    },
    gasUsed: totalGas,
    durationMs: Date.now() - start,
    txHashes,
  };
}
